import { onBeforeUnmount, toValue, watch, type MaybeRefOrGetter } from 'vue'

// Реестр закрываемых слоёв. Раньше каждый слой вешал свой слушатель на document и про
// соседей не знал: одно нажатие Escape схлопывало все открытые слои разом. Теперь
// слушатель ОДИН, слои лежат стеком, и Escape достаётся только верхнему.
type Layer = (event: KeyboardEvent) => void

// Реестр хранит обёртки, а не сами функции: одна и та же ссылка может лежать в стеке
// дважды, и поиск по функции снял бы верхнее вхождение вместо своего. Свежий объект на
// каждый вызов делает indexOf однозначным.
type Entry = { handler: Layer }

const layers: Entry[] = []

function onKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Escape') return
  layers[layers.length - 1]?.handler(event)
}

function syncListener(): void {
  if (layers.length === 1) document.addEventListener('keydown', onKeydown)
  if (layers.length === 0) document.removeEventListener('keydown', onKeydown)
}

// Вход для кода вне Vue-компонента (lib/gallery.ts — лайтбокс на чистом TS).
// Возвращает функцию снятия слоя.
export function pushEscapeLayer(handler: Layer): () => void {
  const entry: Entry = { handler }
  layers.push(entry)
  syncListener()

  let removed = false
  return () => {
    if (removed) return
    removed = true
    const index = layers.indexOf(entry)
    if (index !== -1) layers.splice(index, 1)
    syncListener()
  }
}

// Композабл поверх того же реестра. active управляет тем, лежит ли слой в стеке:
// по умолчанию слой живёт весь mount, но модалка или панель может отдать сюда своё
// «пока открыто» — именно этого умения не хватало прежней версии, из-за чего UiModal
// и UiMenu держали собственные слушатели.
export function useEscape(onEscape: Layer, active: MaybeRefOrGetter<boolean> = true): void {
  let remove: (() => void) | null = null

  const detach = () => {
    remove?.()
    remove = null
  }

  watch(
    () => toValue(active),
    (isActive) => {
      detach()
      if (isActive) remove = pushEscapeLayer(onEscape)
    },
    { immediate: true },
  )

  onBeforeUnmount(detach)
}
